import * as React from 'react';
import { Box } from '@mui/material';
import { EmployeeOffboardingAppointmentHandling } from '@prisma/client';
import DropdownMenu from '../global/DropdownMenu';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import useCampaignActions from '../../hooks/marketing/useCampaignActions';
import { Offboarding } from '../../types/offboardings';
import { MarketingCampaignWithSource } from '../../types/marketing';
import { Feature } from '../../types/subscriptions';
import useCampaignEditor from '../../hooks/marketing/useCampaignEditor';
import CampaignSideBarModal from '../marketing/CampaignSideBarModal';

interface Props {
  offboarding: Offboarding;
  employeeName: string;
}

function OffboardingCampaignActionMenu({ offboarding, employeeName }: Props) {
  const hasMarketingAccess = useFeatureGuard(Feature.MARKETING);
  const editor = useCampaignEditor();

  const { handlers, isPending: disableActions } = useCampaignActions({
    openEditor: editor.open,
  });

  const isStay =
    offboarding.appointmentHandling ===
    EmployeeOffboardingAppointmentHandling.STAY_AT_SHOP;

  const linkedCampaign: MarketingCampaignWithSource | null = offboarding
    .campaign?.source
    ? (offboarding.campaign as unknown as MarketingCampaignWithSource)
    : null;

  const action: 'create' | 'view' | null =
    !isStay || !hasMarketingAccess ? null : linkedCampaign ? 'view' : 'create';

  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Linked campaign deleted while sidebar was open → close it.
  React.useEffect(() => {
    if (sidebarOpen && !linkedCampaign) setSidebarOpen(false);
  }, [sidebarOpen, linkedCampaign]);

  if (!action) return null;

  const label = action === 'view' ? 'View campaign' : 'Create campaign';
  const onAction = () => {
    if (action === 'view') {
      setSidebarOpen(true);
    } else {
      editor.open({
        offboardingContext: { offboardingId: offboarding.id, employeeName },
      });
    }
  };

  return (
    <Box sx={{ ml: 'auto' }}>
      <DropdownMenu
        options={[{ label, action: onAction }]}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
      {sidebarOpen ? (
        <CampaignSideBarModal
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          campaign={linkedCampaign}
          actionHandlers={handlers}
          disableActions={disableActions}
        />
      ) : null}
    </Box>
  );
}

export default OffboardingCampaignActionMenu;
